import {DEFAULT_AGENT_MODE, isAgentMode, type AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {isReasoningEffort, type ReasoningEffort} from '@github-ui/copilot-shared-ui/utils/reasoning-effort'
import {useSyncExternalStore} from 'react'
import {AGENT_TASKS_REASONING_EFFORT_KEY, AGENT_TASKS_SELECTED_MODEL_KEY} from '../task/storage-keys'
import {notifyStorageKeyChanged, subscribeToStorageKey} from '../task/same-tab-storage'

const SELECTED_MODE_STORAGE_KEY = 'quick-chat:selected-mode'

/** Reads the persisted model id, guarding against SSR and storage errors. */
export function readStoredModelId(): string | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    return window.localStorage.getItem(AGENT_TASKS_SELECTED_MODEL_KEY) ?? undefined
  } catch {
    return undefined
  }
}

/** Persists the selected model id, ignoring SSR and storage errors. */
export function writeStoredModelId(modelId: string): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(AGENT_TASKS_SELECTED_MODEL_KEY, modelId)
    notifyStorageKeyChanged(AGENT_TASKS_SELECTED_MODEL_KEY)
  } catch {
    // best-effort
  }
}

/** Reads the persisted reasoning effort, guarding against SSR, storage errors, and stale/invalid values. */
export function readStoredReasoningEffort(): ReasoningEffort | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const value = window.localStorage.getItem(AGENT_TASKS_REASONING_EFFORT_KEY)
    return isReasoningEffort(value) ? value : undefined
  } catch {
    return undefined
  }
}

/**
 * Persists the selected reasoning effort, ignoring SSR and storage errors.
 * `undefined` (the default/no-override level) clears the persisted value.
 */
export function writeStoredReasoningEffort(effort: ReasoningEffort | undefined): void {
  if (typeof window === 'undefined') return
  try {
    if (effort) {
      window.localStorage.setItem(AGENT_TASKS_REASONING_EFFORT_KEY, effort)
    } else {
      window.localStorage.removeItem(AGENT_TASKS_REASONING_EFFORT_KEY)
    }
    notifyStorageKeyChanged(AGENT_TASKS_REASONING_EFFORT_KEY)
  } catch {
    // best-effort
  }
}

/** Reads the persisted agent mode, guarding against SSR, storage errors, and stale/invalid values. */
export function readStoredMode(): AgentMode | undefined {
  if (typeof window === 'undefined') return undefined
  try {
    const value = window.localStorage.getItem(SELECTED_MODE_STORAGE_KEY)
    return isAgentMode(value) ? value : undefined
  } catch {
    return undefined
  }
}

/** Persists the selected agent mode, ignoring SSR and storage errors. */
export function writeStoredMode(mode: AgentMode): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(SELECTED_MODE_STORAGE_KEY, mode)
    notifyStorageKeyChanged(SELECTED_MODE_STORAGE_KEY)
  } catch {
    // best-effort
  }
}

function subscribeToStoredMode(callback: () => void) {
  return subscribeToStorageKey(SELECTED_MODE_STORAGE_KEY, callback)
}

function getStoredModeSnapshot(): AgentMode {
  return readStoredMode() ?? DEFAULT_AGENT_MODE
}

export function getStoredModeServerSnapshot(): AgentMode {
  return DEFAULT_AGENT_MODE
}

/** Reads the persisted agent mode without changing the server's initial hydration output. */
export function useStoredMode(): [AgentMode, (mode: AgentMode) => void] {
  const mode = useSyncExternalStore(subscribeToStoredMode, getStoredModeSnapshot, getStoredModeServerSnapshot)
  return [mode, writeStoredMode]
}
